import type { Editor } from '@tiptap/core';
import { useCallback, useEffect } from 'react';
import type { MutableRefObject } from 'react';

import type { TiptapCellEditingSession } from '../../../../../common/table/TableTypes';
import type { TiptapContent } from '../../../../../common/tiptap/tiptapTypes';
import type { MNHotInstance } from '../../bridge/tableHotInstanceTypes';

/**
 * Keeps Handsontable's shortcut context aligned with the focused Tiptap cell editor.
 */
export const useTiptapCellEditorShortcutContext = ({
    cellEditingSession,
    editor,
    hotTableInstanceRef,
}: {
    cellEditingSession: TiptapCellEditingSession | null;
    editor: Editor | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance<TiptapContent, TiptapContent> | null>;
}) => {
    const activateEditorShortcutContext = useCallback(() => {
        hotTableInstanceRef.current?.getShortcutManager().setActiveContextName('editor');
    }, [hotTableInstanceRef]);

    // Handsontable incorrectly treats focus in the portalled Tiptap editor as grid focus and switches
    // the shortcut context to grid on iOS. This makes the cell editor unusable while editing tables.
    //
    // The useEffect below overrides this behaviour by restoring editor context after Handsontable handles focus.
    useEffect(() => {
        if (!cellEditingSession || !editor) return;

        const onFocusIn = (event: FocusEvent) => {
            if (!event.target || !editor.view.dom.contains(event.target as Node)) return;

            activateEditorShortcutContext();
        };

        const editorDocument = editor.view.dom.ownerDocument;
        editorDocument.addEventListener('focusin', onFocusIn);

        return () => {
            editorDocument.removeEventListener('focusin', onFocusIn);
        };
    }, [activateEditorShortcutContext, cellEditingSession, editor]);

    return activateEditorShortcutContext;
};
